import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClipboardList, Wrench, Flag, CheckCircle2 } from 'lucide-react';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import AddWorkOrderDialog from './AddWorkOrderDialog';
import AddMaintenanceDialog from './AddMaintenanceDialog';
import { RequestFollowUpForm } from './RequestFollowUpForm';
import { RequestNoActionForm } from './RequestNoActionForm';

const CHOICES = [
  { v: 'work_order', label: 'Work order', hint: 'Plan and schedule the repair', Icon: ClipboardList },
  { v: 'maintenance_log', label: 'Maintenance log', hint: 'The work is already done', Icon: Wrench },
  { v: 'follow_up', label: 'Follow-up', hint: 'Quick check, e.g. verify oil level', Icon: Flag },
  { v: 'no_action', label: 'Acknowledge only', hint: 'No work needed — duplicate, already handled or normal reading', Icon: CheckCircle2, noEquipment: true },
];

const requestNote = (r) => `${r.description}\n\nFrom maintenance request by ${r.submitted_by_name} (${new Date(r.observed_at || r.submitted_at).toLocaleString()})`;

const useMachineryOptions = (request) => {
  const [machines, setMachines] = useState([]);
  useEffect(() => {
    const url = request.plant_location_id ? `${API}/locations/${request.plant_location_id}/machinery` : `${API}/machinery`;
    axios.get(url).then((res) => setMachines(res.data || [])).catch(() => setMachines([]));
  }, [request]);
  return machines;
};

const AcknowledgeRequestDialog = ({ request, onClose, onDone }) => {
  const machines = useMachineryOptions(request);
  const [machineryId, setMachineryId] = useState('');
  const [choice, setChoice] = useState(null);
  const [points, setPoints] = useState([]);

  useEffect(() => {
    const name = (request.equipment_name || '').trim().toLowerCase();
    const match = machines.find((m) => (m.component || '').trim().toLowerCase() === name);
    if (match) setMachineryId((cur) => cur || match.id);
  }, [machines, request]);

  useEffect(() => {
    if (choice !== 'maintenance_log' || !machineryId) return;
    axios.get(`${API}/machinery/${machineryId}/installation-points`).then((r) => setPoints(r.data || [])).catch(() => setPoints([]));
  }, [choice, machineryId]);

  const woInitial = useMemo(() => ({
    title: (request.description || '').split(/\r?\n/)[0].slice(0, 120),
    description: requestNote(request),
  }), [request]);
  const logInitial = useMemo(() => ({ work_description: requestNote(request) }), [request]);

  if (choice === 'work_order') {
    return <AddWorkOrderDialog open machineryId={machineryId} sourceRequestId={request.id} initialValues={woInitial}
      onClose={() => setChoice(null)} onSuccess={onDone} />;
  }
  if (choice === 'maintenance_log') {
    return <AddMaintenanceDialog open machineryId={machineryId} installationPoints={points} sourceRequestId={request.id}
      initialValues={logInitial} onClose={() => setChoice(null)} onSuccess={onDone} />;
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="acknowledge-request-dialog">
        <DialogHeader>
          <DialogTitle>Acknowledge request</DialogTitle>
          <DialogDescription>{request.equipment_name} · reported by {request.submitted_by_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="ack-machinery">Equipment{choice === 'no_action' ? ' (optional)' : ''}</Label>
          <select id="ack-machinery" data-testid="ack-machinery-select" value={machineryId}
            onChange={(e) => setMachineryId(e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
            <option value="">Select equipment…</option>
            {machines.map((m) => <option key={m.id} value={m.id}>{m.component}</option>)}
          </select>
          {!machineryId && choice !== 'no_action' && <p className="text-xs text-amber-700" data-testid="ack-machinery-hint">Pick the equipment to create a work order, log or follow-up. Acknowledge only doesn't need it.</p>}
        </div>
        {choice === 'no_action' ? (
          <RequestNoActionForm request={request} machineryId={machineryId} onBack={() => setChoice(null)} onDone={onDone} />
        ) : choice === 'follow_up' && machineryId ? (
          <RequestFollowUpForm request={request} machineryId={machineryId} onBack={() => setChoice(null)} onDone={onDone} />
        ) : (
          <div className="grid gap-2" data-testid="ack-choice-list">
            <p className="text-sm text-slate-600">What should this request become?</p>
            {CHOICES.map(({ v, label, hint, Icon, noEquipment }) => (
              <button key={v} type="button" disabled={!machineryId && !noEquipment} onClick={() => setChoice(v)} data-testid={`ack-choice-${v}`}
                className="flex items-center gap-3 rounded-md border px-3 py-3 text-left transition-colors hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <Icon size={18} className="text-slate-700 shrink-0" />
                <span><span className="block font-medium text-slate-900">{label}</span>
                  <span className="block text-xs text-slate-600">{hint}</span></span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AcknowledgeRequestDialog;
